import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, Copy, RefreshCw, CheckCircle2, AlertCircle, LogOut, Sun, Moon, Award, Sparkles, Check } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { api, authedApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const Verify = () => {
  const { user, ready, userToken, verifyHabbo, regenerateCode, userLogout, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const [memberships, setMemberships] = useState([]);
  const [pointsInfo, setPointsInfo] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [glowBusy, setGlowBusy] = useState(null);

  const loadGlowData = async () => {
    if (!userToken) return;
    try {
      const [mp, shop] = await Promise.all([api.myPoints(userToken), api.listShop()]);
      setPointsInfo(mp);
      setShopItems(shop || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user?.habbo_name) return;
    (async () => {
      try {
        const projects = await api.listProjects();
        const me = user.habbo_name;
        const mine = projects
          .filter((p) => (p.members || []).some((m) => m.habbo_name === me))
          .map((p) => {
            const m = p.members.find((x) => x.habbo_name === me);
            return { ...p, role: m?.role || "" };
          });
        setMemberships(mine);
        // Aktuell gewaehltes Kommentar-Badge laden
        try {
          const a = authedApi(userToken);
          const ab = await a.myAvailableBadges();
          setSelectedBadge(ab.selected || null);
        } catch { /* ignore */ }
      } catch { /* ignore */ }
      await loadGlowData();
    })();
  }, [user?.habbo_name, userToken]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const onSelectBadge = async (code) => {
    try {
      const a = authedApi(userToken);
      await a.setCommentBadge(code);
      setSelectedBadge(code);
      toast({ title: code ? "Kommentar-Badge gesetzt" : "Badge entfernt" });
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  if (!ready) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-zinc-400">
        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!userToken) return <Navigate to="/login" replace />;

  const code = user?.verification_code || "…";
  const verified = user?.verified;
  const avatar = user?.habbo_name
    ? `https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(user.habbo_name)}&direction=2&head_direction=3&size=l&action=std&gesture=sml`
    : null;

  const onCopy = () => {
    navigator.clipboard.writeText(code);
    toast({ title: "Code kopiert" });
  };

  const onVerify = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await verifyHabbo();
      setResult(r);
      if (r.verified) toast({ title: "Habbo verifiziert", description: "Du kannst jetzt kommentieren." });
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "Verifizierung fehlgeschlagen", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const onRegen = async () => {
    if (!window.confirm("Neuen Code generieren? Der alte wird ungültig.")) return;
    setBusy(true);
    try { await regenerateCode(); toast({ title: "Neuer Code erzeugt" }); }
    finally { setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-2xl px-4 py-10">
      <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">Mein Profil</div>
            <h1 className="mt-1 text-2xl font-bold text-white">Hallo {user?.habbo_name}</h1>
            {verified ? (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> Habbo verifiziert
              </div>
            ) : (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#ffb84d]/30 bg-[#ffb84d]/10 px-3 py-1 text-xs text-[#ffb84d]">
                <AlertCircle className="h-3.5 w-3.5" /> Nicht verifiziert
              </div>
            )}
          </div>
          <Button onClick={userLogout} className="btn-ghost rounded-lg px-3 py-2 text-sm">
            <LogOut className="mr-1 h-4 w-4" /> Logout
          </Button>
        </div>

        {avatar && (
          <div className="mt-6 flex items-center gap-4 rounded-xl border border-white/10 bg-[#10141b] p-4">
            <img src={avatar} alt={user?.habbo_name} className="hb-pixel-img h-24" />
            <div>
              <div className="text-sm font-semibold text-white">{user?.habbo_name}</div>
              {user?.verified && <div className="text-xs text-emerald-400">Verifiziert</div>}
            </div>
          </div>
        )}

        {!verified && (
          <div className="mt-8 space-y-4">
            <h2 className="text-lg font-semibold text-white">Habbo verifizieren</h2>
            <ol className="list-decimal space-y-2 pl-5 text-sm text-zinc-300">
              <li>Logge dich auf <a href="https://www.habbo.de" target="_blank" rel="noreferrer" className="text-[#b39bff] underline">habbo.de</a> ein.</li>
              <li>Öffne dein Profil und setze deine <strong>Mission</strong> auf den unten stehenden Code.</li>
              <li>Klicke unten auf <strong>„Jetzt verifizieren“</strong>. Wir prüfen automatisch dein Habbo-Motto.</li>
            </ol>

            <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-[#10141b] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">Dein Code</div>
                <div className="pixel-text mt-1 text-xl text-[#ffb84d]">{code}</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={onCopy} className="btn-ghost rounded-lg px-3 py-2 text-sm"><Copy className="mr-1 h-4 w-4" /> Kopieren</Button>
                <Button onClick={onRegen} disabled={busy} className="btn-ghost rounded-lg px-3 py-2 text-sm"><RefreshCw className="mr-1 h-4 w-4" /> Neu</Button>
              </div>
            </div>

            <Button onClick={onVerify} disabled={busy} className="btn-primary w-full rounded-lg py-3 text-sm font-semibold">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Jetzt verifizieren
            </Button>

            {result && !result.verified && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                <div className="font-semibold">Code wurde nicht im Motto gefunden.</div>
                <div className="mt-1 text-xs">Aktuelles Motto: <span className="text-white">{result.motto || "(leer)"}</span></div>
                <div className="mt-1 text-xs text-zinc-400">{result.hint}</div>
              </div>
            )}
          </div>
        )}

        {verified && (
          <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
            <div className="font-semibold text-emerald-200">Du bist verifiziert!</div>
            <p className="mt-1 text-sm text-emerald-200/80">
              Du kannst jetzt unter News-Artikeln kommentieren – mit deinem Habbo-Avatar.
            </p>
            <Link to="/news" className="mt-3 inline-block text-sm text-[#b39bff] hover:text-white">→ Zu den News</Link>
          </div>
        )}

        {/* Meine Mitgliedschaften – Badge-Picker fuer Kommentare */}
        {verified && memberships.length > 0 && (
          <div className="mt-6 rounded-xl border border-white/10 bg-[#10141b] p-5" data-testid="my-memberships">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
              <Award className="h-4 w-4 text-[#b39bff]" /> Mein Kommentar-Badge
            </h2>
            <p className="mt-1 text-xs text-zinc-400">Waehle, welches deiner Projekt-Badges in deinen Kommentaren erscheinen soll.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                data-testid="badge-pick-none"
                onClick={() => onSelectBadge(null)}
                className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  !selectedBadge ? "border-amber-400/60 bg-amber-500/10" : "border-white/10 bg-[#0f141b] hover:border-white/25"
                }`}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[#10141b]">
                  <Award className="h-5 w-5 text-zinc-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-white">Kein Badge</div>
                  <div className="text-xs text-zinc-400">Automatisch (erstes verfuegbares)</div>
                </div>
                {!selectedBadge && <Check className="h-4 w-4 text-amber-300" />}
              </button>
              {memberships.map((p) => {
                const active = selectedBadge === p.badge_code;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => p.badge_code && onSelectBadge(p.badge_code)}
                    disabled={!p.badge_code}
                    data-testid={`badge-pick-${p.badge_code || p.slug}`}
                    className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                      active
                        ? "border-amber-400/60 bg-amber-500/10"
                        : "border-white/10 bg-[#0f141b] hover:border-white/25 disabled:cursor-not-allowed disabled:opacity-60"
                    }`}
                  >
                    <div className="relative shrink-0 h-12 w-12 flex items-center justify-center">
                      {p.badge_code && (
                        <div aria-hidden className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full blur-md pointer-events-none"
                          style={{ background: `radial-gradient(circle, ${p.badge_color || "#7c4dff"}cc 0%, ${p.badge_color || "#7c4dff"}00 70%)` }} />
                      )}
                      {p.badge_code ? (
                        <img src={`https://images.habbo.com/c_images/album1584/${p.badge_code}.gif`} alt={p.badge_code}
                          className="hb-pixel-img relative h-10 w-10 rounded-full z-10"
                          style={{ boxShadow: `0 0 10px ${p.badge_color || "#7c4dff"}` }} />
                      ) : (
                        <Award className="h-7 w-7 text-zinc-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-white">{p.title}</div>
                      {p.role && <div className="truncate text-xs text-[#b39bff]">{p.role}</div>}
                      {p.badge_code && <div className="text-[10px] uppercase tracking-wider text-zinc-500">{p.badge_code}</div>}
                    </div>
                    {active && <Check className="h-4 w-4 text-amber-300 shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 text-[10px] text-zinc-500">
              Tipp: Du kannst die Projekte auch direkt unter <a href="/projekte" className="text-[#b39bff] hover:underline">/projekte</a> öffnen.
            </div>
          </div>
        )}

        {/* Punkte & freigeschaltete Glows */}
        {verified && pointsInfo && (
          <MyGlowsSection
            pointsInfo={pointsInfo}
            shopItems={shopItems}
            user={user}
            glowBusy={glowBusy}
            onActivate={async (slug) => {
              setGlowBusy(slug || "off");
              try {
                await api.setActiveGlow(userToken, slug);
                toast({ title: slug ? "Glow aktiviert" : "Glow deaktiviert" });
                await loadGlowData();
                await refreshUser(userToken);
              } catch (err) {
                toast({ title: "Fehler", description: err?.response?.data?.detail || "", variant: "destructive" });
              } finally { setGlowBusy(null); }
            }}
          />
        )}

        {/* Einstellungen */}
        <div className="mt-8 rounded-xl border border-white/10 bg-[#10141b] p-5" data-testid="user-settings">
          <h2 className="text-lg font-semibold text-white">Einstellungen</h2>
          <p className="mt-1 text-xs text-zinc-400">Hier kannst du das Design der Seite umstellen. Wird auf allen deinen Geraeten synchronisiert.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => { setTheme("dark"); toast({ title: "Dunkler Modus aktiv" }); }}
              data-testid="theme-dark-btn"
              className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition-colors ${theme === "dark" ? "border-[#7c4dff] bg-[#7c4dff]/15 text-white" : "border-white/10 bg-[#0f141b] text-zinc-300 hover:border-white/25"}`}
            >
              <Moon className="h-4 w-4" /> Dunkel
              {theme === "dark" && <CheckCircle2 className="ml-1 h-3.5 w-3.5 text-[#7c4dff]" />}
            </button>
            <button
              type="button"
              onClick={() => { setTheme("light"); toast({ title: "Heller Modus aktiv" }); }}
              data-testid="theme-light-btn"
              className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition-colors ${theme === "light" ? "border-amber-600 bg-amber-100 text-amber-900" : "border-white/10 bg-[#0f141b] text-zinc-300 hover:border-white/25"}`}
            >
              <Sun className={`h-4 w-4 ${theme === "light" ? "text-amber-700" : ""}`} /> Hell
              {theme === "light" && <CheckCircle2 className="ml-1 h-3.5 w-3.5 text-amber-700" />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const MyGlowsSection = ({ pointsInfo, shopItems, user, glowBusy, onActivate }) => {
  const unlocked = pointsInfo?.unlocked_glows || [];
  const activeSlug = user?.active_glow_slug;
  const myGlowItems = shopItems.filter((it) => unlocked.includes(it.slug));
  const tier = pointsInfo?.tier;

  return (
    <div className="mt-6 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5" data-testid="my-glows">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
        <Sparkles className="h-4 w-4 text-amber-300" /> Meine Punkte &amp; Glows
      </h2>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <div>
          <div className="pixel-text text-3xl text-amber-300" data-testid="my-points-total">{pointsInfo.points.toLocaleString("de-DE")}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Punkte</div>
        </div>
        {tier && (
          <div>
            <div className="text-base font-bold" style={{ color: tier.color }}>{tier.icon} {tier.label}</div>
            {pointsInfo.next_tier && (
              <div className="text-[10px] text-zinc-500">
                noch {pointsInfo.next_tier.min - pointsInfo.points} bis {pointsInfo.next_tier.label}
              </div>
            )}
          </div>
        )}
        <Link to="/shop" className="ml-auto rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20">
          → Zum Glow-Shop
        </Link>
      </div>

      <div className="mt-5 border-t border-white/5 pt-4">
        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
          Freigeschaltet ({myGlowItems.length})
        </div>
        {myGlowItems.length === 0 ? (
          <div className="rounded-md border border-dashed border-white/10 p-4 text-center text-xs text-zinc-500">
            Du hast noch keine Glows freigeschaltet. <Link to="/shop" className="text-amber-300 underline">Schau im Shop vorbei</Link>.
          </div>
        ) : (
          <div className="space-y-2">
            {/* "Aus"-Option */}
            <button
              type="button"
              data-testid="glow-deactivate"
              onClick={() => onActivate(null)}
              disabled={glowBusy === "off"}
              className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${!activeSlug ? "border-amber-400/40 bg-amber-500/10" : "border-white/10 bg-[#0f141b] hover:border-white/25"}`}
            >
              <div className="h-10 w-10 flex-shrink-0 rounded-full border border-white/10 bg-[#10141b] flex items-center justify-center text-xs text-zinc-500">aus</div>
              <div className="flex-1 text-sm text-white">Kein Glow</div>
              {!activeSlug && <Check className="h-4 w-4 text-amber-300" />}
            </button>
            {myGlowItems.map((it) => {
              const isActive = activeSlug === it.slug;
              return (
                <button
                  key={it.id}
                  type="button"
                  data-testid={`glow-pick-${it.slug}`}
                  onClick={() => onActivate(it.slug)}
                  disabled={glowBusy === it.slug || isActive}
                  className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${isActive ? "border-amber-400/60 bg-amber-500/[0.08]" : "border-white/10 bg-[#0f141b] hover:border-white/25"}`}
                >
                  <div
                    className="relative h-10 w-10 flex-shrink-0 rounded-full"
                    style={it.gradient ? { background: it.gradient, boxShadow: `0 0 12px ${it.color}` } : { background: it.color, boxShadow: `0 0 12px ${it.color}` }}
                  >
                    <span className="absolute inset-0 flex items-center justify-center text-sm">{it.icon}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-white">{it.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{it.season || it.category}</div>
                  </div>
                  {isActive ? (
                    <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold text-amber-200">
                      <Check className="mr-1 inline h-3 w-3" /> Aktiv
                    </span>
                  ) : glowBusy === it.slug ? (
                    <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
                  ) : (
                    <span className="text-[10px] text-zinc-500">Anwenden</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Verify;
